import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPOSTWithStatus } from "../apiHelper";
import { projektnaPomoc } from "../listOfApi";
import DeminimisAddForm from "./deminimis/DeminimisAddForm";
import DeminimisPageLayout from "./deminimis/DeminimisPageLayout";
import useDeminimisOptions from "./deminimis/useDeminimisOptions";
import { normalizeDateToInput, parseAmount } from "./deminimis/deminimisUtils";
import RoleGuard from "../auth/RoleGuard";
import { MODULES, ROLE_GROUPS } from "../auth/roles";
import useRoleCheck from "../auth/useRoleCheck";
import AccessDenied from "./ui/AccessDenied";

const emptyFormData = {
  prejemnikID: "",
  dajalecID: "",
  projektID: "",
  datumNacrtovanePomoci: "",
  nacrtovanaVrednost: "",
  datumOdobritve: "",
  visinaPomoci: "",
  pravnaPodlaga: "",
  stevilkaPriglasitve: "",
};

const DeminimisAddPage = ({ role }) => {
  const navigate = useNavigate();
  const { isFakultetniSkrbnik } = useRoleCheck(MODULES.DEMINIMIS);

  // Skrbnik users are always restricted to their own clanica regardless of deminimis level
  const isRestrictedToOwnClanica = isFakultetniSkrbnik;

  const {
    userClanicaId,
    defaultOptions,
    prejemnikDefaultOptions,
    dajalecDefaultOptions,
    fetchPrejemnikOptions,
    fetchDajalecOptions,
    fetchProjektOptions,
  } = useDeminimisOptions(role);

  const [pomocOdobrena, setPomocOdobrena] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deminimisData, setDeminimisData] = useState(emptyFormData);

  const restrictedPrejemnikId = useMemo(() => {
    if (!isRestrictedToOwnClanica) {
      return "";
    }

    if (
      userClanicaId !== undefined &&
      userClanicaId !== null &&
      String(userClanicaId) !== ""
    ) {
      return String(userClanicaId);
    }

    const roleClanicaName = String(
      role?.clanica || role?.clanicaNaziv || "",
    ).trim();
    if (!roleClanicaName) {
      return "";
    }

    const matchedOption = (
      Array.isArray(prejemnikDefaultOptions) ? prejemnikDefaultOptions : []
    ).find(
      (option) =>
        String(option.name || "")
          .trim()
          .toLowerCase() === roleClanicaName.toLowerCase(),
    );

    return matchedOption ? String(matchedOption.value) : "";
  }, [
    isRestrictedToOwnClanica,
    prejemnikDefaultOptions,
    role?.clanica,
    role?.clanicaNaziv,
    userClanicaId,
  ]);

  useEffect(() => {
    if (!isRestrictedToOwnClanica || !restrictedPrejemnikId) {
      return;
    }

    setDeminimisData((prevState) => ({
      ...prevState,
      prejemnikID: restrictedPrejemnikId,
    }));
  }, [isRestrictedToOwnClanica, restrictedPrejemnikId]);

  const resolvedPrejemnikOptions = useMemo(() => {
    const normalizedOptions = Array.isArray(prejemnikDefaultOptions)
      ? prejemnikDefaultOptions
      : [];

    if (!isRestrictedToOwnClanica || !restrictedPrejemnikId) {
      return normalizedOptions;
    }

    const clanicaValue = String(restrictedPrejemnikId);
    const hasUserClanicaOption = normalizedOptions.some(
      (option) => String(option.value) === clanicaValue,
    );

    if (hasUserClanicaOption) {
      return normalizedOptions;
    }

    const fallbackClanicaName = String(
      role?.clanica || role?.clanicaNaziv || "Moja članica",
    ).trim();

    return [
      ...normalizedOptions,
      {
        name: fallbackClanicaName || clanicaValue,
        value: clanicaValue,
      },
    ];
  }, [
    isRestrictedToOwnClanica,
    prejemnikDefaultOptions,
    restrictedPrejemnikId,
    role,
  ]);

  const resolvedPrejemnikLabel = useMemo(() => {
    if (!isRestrictedToOwnClanica) {
      return "";
    }

    const selectedValue = String(
      deminimisData.prejemnikID || restrictedPrejemnikId || "",
    );

    if (!selectedValue) {
      return String(
        role?.clanica || role?.clanicaNaziv || "Moja članica",
      ).trim();
    }

    const selectedOption = resolvedPrejemnikOptions.find(
      (option) => String(option.value) === selectedValue,
    );

    return String(
      selectedOption?.name ||
        role?.clanica ||
        role?.clanicaNaziv ||
        selectedValue,
    ).trim();
  }, [
    deminimisData.prejemnikID,
    isRestrictedToOwnClanica,
    resolvedPrejemnikOptions,
    restrictedPrejemnikId,
    role,
  ]);

  const wrappedFetchProjektOptions = useMemo(() => {
    return (searchTerm, oldOptions) => {
      const clanicaId = deminimisData.prejemnikID || "";
      return fetchProjektOptions(clanicaId, searchTerm, oldOptions);
    };
  }, [deminimisData.prejemnikID, fetchProjektOptions]);

  const handleFormData = (e) => {
    setDeminimisData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handleProjektChange = (selectedValue, selectedOption) => {
    const normalizedZacetek = normalizeDateToInput(selectedOption?.zacetek);

    setDeminimisData((prevState) => ({
      ...prevState,
      projektID: selectedValue,
      datumNacrtovanePomoci:
        normalizedZacetek || prevState.datumNacrtovanePomoci,
    }));
  };

  const handlePomocOdobrenaChange = (checked) => {
    setPomocOdobrena(checked);

    if (!checked) {
      setDeminimisData((prevState) => ({
        ...prevState,
        datumOdobritve: "",
        visinaPomoci: "",
        pravnaPodlaga: "",
        stevilkaPriglasitve: "",
      }));
    }
  };

  const handlePrejemnikChange = (selectedValue) => {
    if (isRestrictedToOwnClanica) {
      return;
    }

    setDeminimisData((prevState) => ({
      ...prevState,
      prejemnikID: selectedValue,
      projektID: "",
    }));
  };

  const handleDajalecChange = (selectedValue) => {
    setDeminimisData((prevState) => ({
      ...prevState,
      dajalecID: selectedValue,
    }));
  };

  const handleNacrtovanaVrednostChange = (value) => {
    setDeminimisData((prevState) => ({
      ...prevState,
      nacrtovanaVrednost: value || "",
    }));
  };

  const handleVisinaPomociChange = (value) => {
    setDeminimisData((prevState) => ({
      ...prevState,
      visinaPomoci: value || "",
    }));
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    if (isRestrictedToOwnClanica && !restrictedPrejemnikId) {
      setSubmitError("Vaša članica ni določena, zato vnos ni mogoč.");
      return;
    }

    if (!deminimisData.prejemnikID) {
      setSubmitError("Polje Prejemnik državne pomoči je obvezno.");
      return;
    }

    if (!deminimisData.dajalecID) {
      setSubmitError("Polje Dajalec je obvezno.");
      return;
    }

    if (!deminimisData.projektID) {
      setSubmitError("Polje Projekt je obvezno.");
      return;
    }

    const nacrtovanaVrednost = parseAmount(deminimisData.nacrtovanaVrednost);
    if (nacrtovanaVrednost === null || nacrtovanaVrednost < 0) {
      setSubmitError(
        "Načrtovana vrednost mora biti število enako ali večje od 0.",
      );
      return;
    }

    const visinaPomoci = parseAmount(deminimisData.visinaPomoci);

    if (pomocOdobrena) {
      if (!deminimisData.datumOdobritve) {
        setSubmitError("Datum odobritve državne pomoči je obvezen.");
        return;
      }

      if (visinaPomoci === null || visinaPomoci < 0) {
        setSubmitError(
          "Višina odobrene pomoči mora biti število enako ali večje od 0.",
        );
        return;
      }

      if (visinaPomoci > nacrtovanaVrednost) {
        setSubmitError(
          "Višina odobrene pomoči ne sme biti večja od načrtovane vrednosti.",
        );
        return;
      }
    }

    const prejemnikId = Number(
      isRestrictedToOwnClanica
        ? restrictedPrejemnikId
        : deminimisData.prejemnikID,
    );
    const dajalecId = Number(deminimisData.dajalecID);
    const projektID = Number(deminimisData.projektID);

    if (Number.isNaN(prejemnikId)) {
      setSubmitError("Izbran prejemnik ni veljaven.");
      return;
    }

    if (Number.isNaN(dajalecId)) {
      setSubmitError("Izbran dajalec ni veljaven.");
      return;
    }

    if (Number.isNaN(projektID)) {
      setSubmitError("Izbran projekt ni veljaven.");
      return;
    }

    const payload = {
      prejemnikId,
      dajalecId,
      projektID,
      nacrtovanaVrednost,
      pomocOdobrena,
      datumNacrtovanePomoci: deminimisData.datumNacrtovanePomoci,
      datumOdobritve: pomocOdobrena ? deminimisData.datumOdobritve : null,
      visinaOdobrenePomoci: pomocOdobrena ? visinaPomoci : null,
      pravnaPodlaga: deminimisData.pravnaPodlaga,
      stevilkaPriglasitve: deminimisData.stevilkaPriglasitve,
    };

    setIsSubmitting(true);
    try {
      const response = await fetchPOSTWithStatus(projektnaPomoc, payload);
      if (response.status === 201 || response.status === 200) {
        setSubmitSuccess("Vnos projektne pomoči je uspešno shranjen.");
      } else if (response.status === 400) {
        setSubmitError("Vnos ni uspel. Preverite vnesene podatke.");
      } else if (response.status === 404) {
        setSubmitError("Izbran projekt ne obstaja.");
      } else {
        setSubmitError("Prišlo je do napake strežnika.");
      }
    } catch (error) {
      setSubmitError("Prišlo je do napake strežnika.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RoleGuard
      moduleKey={MODULES.DEMINIMIS}
      roles={ROLE_GROUPS.EDITORS_AND_SKRBNIK}
      fallback={<AccessDenied />}
    >
      <DeminimisPageLayout title="De minimis - vnos podatkov" lg={5} md={7}>
        <DeminimisAddForm
          submitError={submitError}
          submitSuccess={submitSuccess}
          isSubmitting={isSubmitting}
          onBack={() => navigate(-1)}
          onSubmit={handleSubmitForm}
          deminimisData={deminimisData}
          pomocOdobrena={pomocOdobrena}
          onFieldChange={handleFormData}
          onPomocOdobrenaChange={handlePomocOdobrenaChange}
          onSelectPrejemnik={handlePrejemnikChange}
          onSelectDajalec={handleDajalecChange}
          onSelectProjekt={handleProjektChange}
          onNacrtovanaVrednostChange={handleNacrtovanaVrednostChange}
          onVisinaPomociChange={handleVisinaPomociChange}
          prejemnikDefaultOptions={resolvedPrejemnikOptions}
          prejemnikLockedLabel={resolvedPrejemnikLabel}
          dajalecDefaultOptions={dajalecDefaultOptions}
          isPrejemnikLocked={isRestrictedToOwnClanica}
          defaultOptions={defaultOptions}
          fetchPrejemnikOptions={fetchPrejemnikOptions}
          fetchDajalecOptions={fetchDajalecOptions}
          fetchProjektOptions={wrappedFetchProjektOptions}
        />
      </DeminimisPageLayout>
    </RoleGuard>
  );
};

export default DeminimisAddPage;
